import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { LoadingContext } from '@/config/loading-messages.config'

export const useAppStore = defineStore(
	'app',
	() => {
		const darkMode = ref(false)
		const sidebarExpanded = ref(true)
		const sidebarMobileOpen = ref(false)

		// Global blocking-loader state. Ref-counted so concurrent operations can
		// overlap: the overlay shows while count > 0 and hides when it returns to 0.
		// Prefer the useGlobalLoader composable over calling these directly.
		const loadingCount = ref(0)
		const loadingContext = ref<LoadingContext>('generic')
		const loading = computed(() => loadingCount.value > 0)

		function showLoader(context: LoadingContext = 'generic') {
			// First show wins the message context; nested shows keep the current one
			if (loadingCount.value === 0) {
				loadingContext.value = context
			}
			loadingCount.value++
		}

		function hideLoader() {
			loadingCount.value = Math.max(0, loadingCount.value - 1)
		}

		/** Back-compat shim for the old boolean API. */
		function setLoading(value: boolean) {
			if (value) {
				showLoader()
			} else {
				hideLoader()
			}
		}

		function toggleDarkMode() {
			darkMode.value = !darkMode.value
		}

		function toggleSidebar() {
			sidebarExpanded.value = !sidebarExpanded.value
		}

		function openMobileSidebar() {
			sidebarMobileOpen.value = true
		}

		function closeMobileSidebar() {
			sidebarMobileOpen.value = false
		}

		function $reset() {
			darkMode.value = false
			loadingCount.value = 0
			loadingContext.value = 'generic'
			sidebarExpanded.value = true
			sidebarMobileOpen.value = false
		}

		return {
			darkMode,
			loading,
			loadingCount,
			loadingContext,
			sidebarExpanded,
			sidebarMobileOpen,
			showLoader,
			hideLoader,
			toggleDarkMode,
			setLoading,
			toggleSidebar,
			openMobileSidebar,
			closeMobileSidebar,
			$reset,
		}
	},
	{
		persist: {
			pick: ['darkMode', 'sidebarExpanded'],
		},
	},
)
